import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Entity, ColorTokens } from '@shapeable/types';
import { APPLY_COLORS, breakpoints, theme } from '@shapeable/theme';
import { classNames, firstImageProperty, firstStringProperty } from '@shapeable/utils';
import { ContentNode, defaultEntityCardDescriptionPaths, EntityBubbleImage, EntityTypeHeader, useLink } from '@shapeable/ui';
const cls = classNames('trend-card');

// -------- Types -------->

// -------- Props -------->

export type EntityPublicationCardProps = HasChildren & Classable & { 
  entity: Entity;
  colorStripColor?: string;
  foot?: React.ReactNode;
  colors?: ColorTokens.TextBackground;
};

export const EntityPublicationCardDefaultProps: Omit<EntityPublicationCardProps, 'entity'> = {
  colorStripColor: 'primary',
  colors: {
    text: 'dark',
    background: 'canvas',
  }
};

// -------- Child Component Props -------->

type ContainerProps = {
  colors?: ColorTokens.TextBackground;
  hasLink?: boolean;
}

type LayoutProps = {
  $colorStripColor: string;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    width: 100%;
    border-radius: ${theme.UNIT(3)};
    border: 1px solid ${theme.COLOR('line-subtle')};
    overflow: hidden;

    ${theme.APPLY_COLORS};

    ${({ hasLink }: ContainerProps ) => hasLink && css`
      ${theme.TRANSITION('box-shadow', 'transform')};

      &:hover {
        box-shadow: 0 8px 20px -6px ${theme.COLOR('subtle-shadow')};
        transform: translateY(-2px);
      }
    `}
  `,
});

const LayoutStyles = breakpoints({
  base: css`
    padding: ${theme.UNIT(5)};
    ${({ $colorStripColor }: LayoutProps ) => css`
      border-top: 5px solid ${theme.COLOR($colorStripColor)};
    `}
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.UNIT(5)};
    flex: 1;
  `,
});

const ContentStyles = breakpoints({
  base: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(6)};
  `,
});

const HeaderStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
  `,
});

const TypeHeaderStyles = breakpoints({
  base: css`
    
  `,
});

const TitleStyles = breakpoints({
  base: css`
    margin: 0;
    font-weight: 400;
    font-size: 1.75em;
    color: inherit;
    line-height: 1em;
  `,
});

const ImageStyles = breakpoints({
  base: css`
    border: 3px solid ${theme.COLOR('primary')};
  `,
});

const DescriptionStyles = breakpoints({
  base: css`
    ${theme.MAX_LINES(2)};
    font-weight: 300;
    color: ${theme.COLOR('text')};
  `,
});

const FooterStyles = breakpoints({
  base: css`
    
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
  Layout: styled.div<LayoutProps>`${LayoutStyles}`,
  Body: styled.div`${BodyStyles}`,
    Content: styled.div`${ContentStyles}`,
      Header: styled.div`${HeaderStyles}`,
        TypeHeader: styled(EntityTypeHeader)`${TypeHeaderStyles}`,
        Title: styled.h3`${TitleStyles}`,
        Description: styled.div.attrs(cls.attr('description'))`${DescriptionStyles}`,
    BubbleImage: styled(EntityBubbleImage).attrs(cls.attr('bubble-image'))`${ImageStyles}`,
  Footer: styled.div`${FooterStyles}`,
};

export const EntityPublicationCard: Shapeable.FC<EntityPublicationCardProps> = (props) => {
  const { className, entity, foot, colors } = props;

  const { path, url } = entity || {};

  const { Link } = useLink();

  const link = url || path;

  const descriptionText = firstStringProperty(entity, defaultEntityCardDescriptionPaths);
  const titleText = firstStringProperty(entity, ['title', 'name']);
  const hasDescription = descriptionText && !!descriptionText;

  const colorStripColor = entity.color?.value || props.colorStripColor;

  const hasImage = !!firstImageProperty(entity, ['openGraph.image.thumbnails.bubble', 'openGraph.image.thumbnails.card', 'openGraph.image' ]);
  
  const inner = (
    <My.Container hasLink={!!link} colors={colors} className={cls.name(className)}>
      <My.Layout $colorStripColor={colorStripColor} >
      <My.Body>
        <My.Content>
          <My.Header>
            <My.TypeHeader entity={entity} />
            <My.Title>{titleText}</My.Title>
          </My.Header>
          {
            hasDescription && (
              <My.Description>{descriptionText}</My.Description>
            )
          }
        </My.Content>
        {
          hasImage && (
            <My.BubbleImage entity={entity} size={100} />
          )
        }
      </My.Body>
      <My.Footer >
        {foot}
      </My.Footer>
      </My.Layout>
    </My.Container>
  );

  return (
    <>
      {!!link ? (
        <Link href={link} style={{ textDecoration: 'none' }}>
          {inner}
        </Link>
      ) : (
        inner
      )}
    </>
  )
};

EntityPublicationCard.defaultProps = EntityPublicationCardDefaultProps;
EntityPublicationCard.cls = cls;